import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArcButton } from "@/components/ArcButton";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Upload } from "lucide-react";

const BADGES = ["", "VERIFIED", "COMMUNITY LORE", "SATIRE", "UNCONFIRMED"];
const LABELS = ["", "VERIFIED", "COMMUNITY LORE", "SATIRE", "UNCONFIRMED"];
let localCounter = 0;
const uid = () => `local-${Date.now()}-${localCounter++}`;

const EMPTY = {
  slug: "", encyclopedia: "ghoulax", encyclopedias: ["ghoulax"], title: "", subtitle: "", description: "", badge: "",
  intro: "", sections: [], infobox: { title: "", image: "", fields: [] },
  categories: [], references: [], related: [], timeline: [], image: "",
};

export default function ArticleEditor() {
  const { slug } = useParams();
  const isNew = !slug || slug === "new";
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const backTo = isAdmin ? "/admin/articles" : "/editor";
  const [form, setForm] = useState(EMPTY);
  const [ready, setReady] = useState(isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) { setReady(true); return; }
    api.get(`/articles/${slug}`).then((r) => {
      const d = r.data;
      d.sections = (d.sections || []).map((s) => ({ ...s, _k: uid() }));
      d.infobox = d.infobox || { title: "", image: "", fields: [] };
      d.categories = d.categories || [];
      d.references = d.references || [];
      d.related = d.related || [];
      d.timeline = d.timeline || [];
      d.encyclopedias = (d.encyclopedias && d.encyclopedias.length) ? d.encyclopedias : [d.encyclopedia || "ghoulax"];
      setForm(d); setReady(true);
    }).catch(() => { toast.error("Article not found"); navigate(backTo); });
  }, [slug]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTitle = (v) => setForm((f) => ({ ...f, title: v, slug: isNew ? v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : f.slug }));

  // sections
  const addSection = () => set("sections", [...form.sections, { _k: uid(), id: uid(), heading: "New Section", label: "", body: "" }]);
  const updSection = (k, key, val) => set("sections", form.sections.map((s) => s._k === k ? { ...s, [key]: val } : s));
  const delSection = (k) => set("sections", form.sections.filter((s) => s._k !== k));

  // infobox fields
  const addField = () => set("infobox", { ...form.infobox, fields: [...form.infobox.fields, { label: "", value: "" }] });
  const updField = (i, key, val) => set("infobox", { ...form.infobox, fields: form.infobox.fields.map((f, idx) => idx === i ? { ...f, [key]: val } : f) });
  const delField = (i) => set("infobox", { ...form.infobox, fields: form.infobox.fields.filter((_, idx) => idx !== i) });

  // references
  const addRef = () => set("references", [...form.references, { title: "", url: "" }]);
  const updRef = (i, key, val) => set("references", form.references.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const delRef = (i) => set("references", form.references.filter((_, idx) => idx !== i));

  // timeline
  const addTl = () => set("timeline", [...form.timeline, { date: "Date unknown", event: "", description: "", source: "" }]);
  const updTl = (i, key, val) => set("timeline", form.timeline.map((t, idx) => idx === i ? { ...t, [key]: val } : t));
  const delTl = (i) => set("timeline", form.timeline.filter((_, idx) => idx !== i));

  const uploadInfoboxImage = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("infobox", { ...form.infobox, image: data.url });
      toast.success("Image uploaded");
    } catch (err) { toast.error("Upload failed"); }
  };

  const save = async () => {
    if (!form.title.trim() || !form.slug.trim()) { toast.error("Title and slug are required."); return; }
    const encs = ["ghoulax", "zyfley"].filter((e) => (form.encyclopedias || []).includes(e));
    if (encs.length === 0) { toast.error("Pick at least one encyclopedia."); return; }
    setSaving(true);
    const payload = {
      ...form,
      encyclopedias: encs,
      encyclopedia: encs[0],
      badge: form.badge || null,
      infobox: (form.infobox.title || form.infobox.image || form.infobox.fields.length) ? {
        title: form.infobox.title, image: form.infobox.image || null,
        fields: form.infobox.fields.filter((f) => f.label || f.value),
      } : null,
      sections: form.sections.map(({ _k, ...s }) => ({ ...s, label: s.label || null })),
      references: form.references.filter((r) => r.title),
      timeline: form.timeline.filter((t) => t.event),
      related: form.related.filter(Boolean),
      categories: form.categories.filter(Boolean),
    };
    try {
      if (isNew) { await api.post("/articles", payload); toast.success(isAdmin ? "Article created" : "Draft submitted for review"); }
      else { await api.put(`/articles/${slug}`, payload); toast.success(isAdmin ? "Article updated" : "Draft updated & resubmitted for review"); }
      navigate(backTo);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!ready) return <div className="flex min-h-[50vh] items-center justify-center text-sm" style={{ color: "var(--arc-text-2)" }}>Loading…</div>;

  const inputStyle = { backgroundColor: "var(--arc-card)" };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <Link to={backTo} className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: "var(--arc-text-2)" }}><ArrowLeft size={14} /> {isAdmin ? "Articles" : "Editor Workspace"}</Link>
      <h1 className="mt-4 font-serif-arc text-3xl font-black">{isNew ? "New Article" : "Edit Article"}</h1>
      {!isAdmin && <p className="mt-2 text-sm" style={{ color: "var(--arc-text-2)" }}>Your submission will be reviewed by an administrator before it appears in the archive.</p>}

      <div className="mt-8 space-y-8">
        {/* Basics */}
        <Section title="Basics">
          <Field label="Title *"><Input data-testid="editor-title" value={form.title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} /></Field>
          <Field label="Slug *"><Input data-testid="editor-slug" value={form.slug} onChange={(e) => set("slug", e.target.value)} disabled={!isNew} style={inputStyle} /></Field>
          <Field label="Appears in *">
            <Select
              value={(form.encyclopedias || []).length === 2 ? "both" : ((form.encyclopedias || [])[0] || "ghoulax")}
              onValueChange={(v) => set("encyclopedias", v === "both" ? ["ghoulax", "zyfley"] : [v])}
            >
              <SelectTrigger data-testid="editor-encyclopedia" style={inputStyle}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ghoulax">Ghoulax</SelectItem>
                <SelectItem value="zyfley">Zyfley</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1.5 text-xs" style={{ color: "var(--arc-text-2)" }}>In both, the article takes on each section's colour when opened from that side.</p>
          </Field>
          <Field label="Subtitle"><Input data-testid="editor-subtitle" value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} style={inputStyle} /></Field>
          <Field label="Short description (for search & cards)"><Textarea data-testid="editor-description" value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} style={inputStyle} /></Field>
          <Field label="Article badge">
            <Select value={form.badge || "none"} onValueChange={(v) => set("badge", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="editor-badge" style={inputStyle}><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {BADGES.filter(Boolean).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Categories (comma separated)">
            <Input data-testid="editor-categories" value={form.categories.join(", ")} onChange={(e) => set("categories", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} style={inputStyle} />
          </Field>
          <Field label="Related article slugs (comma separated)">
            <Input data-testid="editor-related" value={form.related.join(", ")} onChange={(e) => set("related", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} style={inputStyle} />
          </Field>
        </Section>

        {/* Intro */}
        <Section title="Introduction">
          <RichTextEditor testId="editor-intro" value={form.intro} onChange={(v) => set("intro", v)} />
        </Section>

        {/* Sections */}
        <Section title="Sections" action={<button onClick={addSection} data-testid="add-section" className="inline-flex items-center gap-1 text-sm ghoulax-accent hover:underline"><Plus size={14} /> Add section</button>}>
          <div className="space-y-6">
            {form.sections.map((s) => (
              <div key={s._k} className="rounded-md border p-4" style={{ backgroundColor: "var(--arc-card)" }}>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1"><Label className="mb-1 block text-xs">Heading</Label><Input value={s.heading} onChange={(e) => updSection(s._k, "heading", e.target.value)} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
                  <div className="w-44">
                    <Label className="mb-1 block text-xs">Label</Label>
                    <Select value={s.label || "none"} onValueChange={(v) => updSection(s._k, "label", v === "none" ? "" : v)}>
                      <SelectTrigger style={{ backgroundColor: "var(--arc-bg)" }}><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">None</SelectItem>{LABELS.filter(Boolean).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <button onClick={() => delSection(s._k)} className="flex h-9 w-9 items-center justify-center rounded border text-red-600"><Trash2 size={15} /></button>
                </div>
                <div className="mt-3"><RichTextEditor testId={`section-body-${s._k}`} value={s.body} onChange={(v) => updSection(s._k, "body", v)} /></div>
              </div>
            ))}
            {form.sections.length === 0 && <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>No sections yet.</p>}
          </div>
        </Section>

        {/* Infobox */}
        <Section title="Infobox" action={<button onClick={addField} data-testid="add-field" className="inline-flex items-center gap-1 text-sm ghoulax-accent hover:underline"><Plus size={14} /> Add field</button>}>
          <Field label="Infobox title"><Input value={form.infobox.title || ""} onChange={(e) => set("infobox", { ...form.infobox, title: e.target.value })} style={inputStyle} /></Field>
          <Field label="Infobox image">
            <div className="flex items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm" style={inputStyle}>
                <Upload size={14} /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={uploadInfoboxImage} data-testid="infobox-image-upload" />
              </label>
              {form.infobox.image && <img src={fileUrl(form.infobox.image)} alt="infobox" className="h-12 w-12 rounded object-cover" />}
            </div>
          </Field>
          <div className="space-y-2">
            {form.infobox.fields.map((f, i) => (
              <div key={i} className="flex gap-2">
                <Input placeholder="Label" value={f.label} onChange={(e) => updField(i, "label", e.target.value)} style={inputStyle} />
                <Input placeholder="Value" value={f.value} onChange={(e) => updField(i, "value", e.target.value)} style={inputStyle} />
                <button onClick={() => delField(i)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded border text-red-600"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </Section>

        {/* Timeline */}
        <Section title="Timeline" action={<button onClick={addTl} data-testid="add-timeline" className="inline-flex items-center gap-1 text-sm ghoulax-accent hover:underline"><Plus size={14} /> Add entry</button>}>
          <div className="space-y-3">
            {form.timeline.map((t, i) => (
              <div key={i} className="rounded-md border p-3" style={{ backgroundColor: "var(--arc-card)" }}>
                <div className="flex gap-2">
                  <Input placeholder="Date (or 'Date unknown')" value={t.date} onChange={(e) => updTl(i, "date", e.target.value)} style={{ backgroundColor: "var(--arc-bg)" }} />
                  <Input placeholder="Event" value={t.event} onChange={(e) => updTl(i, "event", e.target.value)} style={{ backgroundColor: "var(--arc-bg)" }} />
                  <button onClick={() => delTl(i)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded border text-red-600"><Trash2 size={15} /></button>
                </div>
                <Textarea placeholder="Description" className="mt-2" rows={2} value={t.description} onChange={(e) => updTl(i, "description", e.target.value)} style={{ backgroundColor: "var(--arc-bg)" }} />
                <Input placeholder="Source" className="mt-2" value={t.source || ""} onChange={(e) => updTl(i, "source", e.target.value)} style={{ backgroundColor: "var(--arc-bg)" }} />
              </div>
            ))}
          </div>
        </Section>

        {/* References */}
        <Section title="References" action={<button onClick={addRef} data-testid="add-reference" className="inline-flex items-center gap-1 text-sm ghoulax-accent hover:underline"><Plus size={14} /> Add reference</button>}>
          <div className="space-y-2">
            {form.references.map((r, i) => (
              <div key={i} className="flex gap-2">
                <Input placeholder="Title" value={r.title} onChange={(e) => updRef(i, "title", e.target.value)} style={inputStyle} />
                <Input placeholder="URL (optional)" value={r.url || ""} onChange={(e) => updRef(i, "url", e.target.value)} style={inputStyle} />
                <button onClick={() => delRef(i)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded border text-red-600"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </Section>

        <div className="flex gap-3 border-t pt-6">
          <ArcButton variant="ghoulax" onClick={save} disabled={saving} data-testid="save-article">{saving ? "Saving…" : (isAdmin ? "Save Article" : "Submit for Review")}</ArcButton>
          <ArcButton variant="outline" to={backTo}>Cancel</ArcButton>
        </div>
      </div>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <section>
      <div className="mb-3 flex items-center justify-between border-b pb-2">
        <h2 className="font-serif-arc text-xl font-bold">{title}</h2>
        {action}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}
